import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Paper, Select, Stack, TextField, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
} from '@mui/icons-material';
import wineListSectionService from '../../services/wineListSectionService';
import type { WineListSection, WineListSectionVisibility } from '../../types/venue';

interface Props {
  venueId: string;
  wineListId: string;
}

interface FormState {
  name: string;
  description: string;
  position: string;
  visibility: WineListSectionVisibility;
}

const emptyForm: FormState = { name: '', description: '', position: '', visibility: 'LIVE' };

/**
 * Admin section management panel — used inside WineListDetail's "Sections" tab.
 * Lists current sections in display order with create / edit / delete actions.
 * Soft delete unassigns items from the section (server-side); items themselves remain.
 */
const WineListSectionsPanel: React.FC<Props> = ({ venueId, wineListId }) => {
  const theme = useTheme();
  const [sections, setSections] = useState<WineListSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WineListSection | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    setLoading(true);
    setError('');
    wineListSectionService.getSections(venueId, wineListId)
      .then(setSections)
      .catch(() => setError('Could not load sections. Please try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [venueId, wineListId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, position: String(sections.length) });
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (s: WineListSection) => {
    setEditing(s);
    setForm({
      name: s.name,
      description: s.description ?? '',
      position: String(s.position ?? 0),
      visibility: s.visibility,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('Name is required.');
      return;
    }
    const positionNum = form.position === '' ? 0 : Number(form.position);
    if (!isFinite(positionNum)) {
      setFormError('Position must be a number.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        position: positionNum,
        visibility: form.visibility,
      };
      if (editing) {
        await wineListSectionService.updateSection(venueId, wineListId, editing._id, payload);
      } else {
        await wineListSectionService.createSection(venueId, wineListId, payload);
      }
      setDialogOpen(false);
      refresh();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'SECTION_NAME_DUPLICATE') {
        setFormError('A section with this name already exists on this list.');
      } else {
        setFormError(err?.response?.data?.message ?? 'Could not save the section. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s: WineListSection) => {
    if (!window.confirm(`Delete section "${s.name}"? Items will be moved to "Other".`)) return;
    try {
      await wineListSectionService.deleteSection(venueId, wineListId, s._id);
      refresh();
    } catch {
      setError('Could not delete the section. Please try again.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Sections</Typography>
          <Typography variant="caption" color="text.secondary">
            Group wines into sections for the customer view. Items can be assigned a section from the wine entry form.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          New section
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 1, mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : sections.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No sections yet. Wines without a section appear under "Other" in the preview.
          </Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openCreate} sx={{ mt: 1, textTransform: 'none' }}>
            Create first section
          </Button>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {sections.map(s => (
            <Paper
              key={s._id}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderColor: s.visibility === 'HIDDEN' ? alpha(theme.palette.text.disabled, 0.3) : undefined,
                opacity: s.visibility === 'HIDDEN' ? 0.7 : 1,
              }}
            >
              <Box sx={{
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 1, backgroundColor: alpha(theme.palette.primary.main, 0.08),
                fontSize: '0.78rem', fontFamily: 'monospace', color: theme.palette.text.secondary,
              }}>
                {s.position}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} noWrap>{s.name}</Typography>
                  {s.visibility === 'HIDDEN' && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontWeight: 600 }}>
                      HIDDEN
                    </Typography>
                  )}
                </Box>
                {s.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.description}
                  </Typography>
                )}
              </Box>
              <Tooltip title="Edit section">
                <IconButton size="small" onClick={() => openEdit(s)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete section">
                <IconButton size="small" color="error" onClick={() => handleDelete(s)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Edit section' : 'New section'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>{formError}</Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              autoFocus
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional. Shown under the section heading on the customer view."
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Position"
                value={form.position}
                onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                type="number"
                sx={{ width: 120 }}
                helperText="Lower = earlier"
              />
              <Select
                value={form.visibility}
                onChange={e => setForm(f => ({ ...f, visibility: e.target.value as WineListSectionVisibility }))}
                sx={{ flex: 1 }}
                size="medium"
              >
                <MenuItem value="LIVE">Live — visible on preview</MenuItem>
                <MenuItem value="HIDDEN">Hidden — not shown</MenuItem>
              </Select>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, minWidth: 100 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WineListSectionsPanel;
